// §9.3 / §10 — capability tokens: 256 bits, minted per holder, never reused.
//
// It sits directly in `kernel/` rather than inside `bridge/` because two services mint tokens for
// the same reason and to the same rules: §9.3's per-window-instance bridge token, and §10's
// per-payload transfer token. A shared leaf keeps the two honest; a copy in each would be two
// chances for one of them to be subtly weaker (§4.2 also forbids the cycle the alternative made).
//
// "Per holder" is the whole point. Two windows of one app hold different tokens, so a
// message forged by one cannot act as the other — and a token dies with its window, which is what
// makes §9.5's "never inherited by a new window" true of the bridge as well as of the filesystem.
import { CAPABILITY_TOKEN_BITS } from '@hangar/shared';

const TOKEN_BYTES = CAPABILITY_TOKEN_BITS / 8;

/** Injected so a test can be deterministic. Production is `crypto.getRandomValues`. */
export type RandomBytes = (length: number) => Uint8Array;

export function browserRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function hex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}

export interface TokenMinter {
  /** A fresh token. Never returns one it has returned before. */
  mint(): string;
  /** True when this minter issued the token and it has not been retired. */
  holds(token: string): boolean;
  retire(token: string): void;
  readonly issued: number;
}

export function createTokenMinter(randomBytes: RandomBytes = browserRandomBytes): TokenMinter {
  const live = new Set<string>();
  /** Every token ever issued, so "never reused" is enforced and not merely intended. */
  const spent = new Set<string>();
  let issued = 0;

  return {
    mint() {
      // A 256-bit collision will not happen; looping on it costs nothing and makes the claim
      // in §9.3 a property of the code rather than of the odds.
      let token = hex(randomBytes(TOKEN_BYTES));
      while (spent.has(token)) token = hex(randomBytes(TOKEN_BYTES));
      spent.add(token);
      live.add(token);
      issued += 1;
      return token;
    },
    holds: (token) => live.has(token),
    retire(token) {
      live.delete(token);
    },
    get issued() {
      return issued;
    },
  };
}

/** A token is 64 lowercase hex characters — 256 bits. Used to reject nonsense early. */
export function isTokenShaped(value: unknown): value is string {
  return typeof value === 'string' && new RegExp(`^[0-9a-f]{${TOKEN_BYTES * 2}}$`).test(value);
}
